import { Navigate, Outlet, useRouterState } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Drawer } from '../../../components/Drawer';
import { ForbiddenState } from '../../../components/system-states/SystemState';
import { useModules } from '../../../lib/modules';
import { useMediaQuery } from '../../../lib/use-media-query';
import { useCurrentSession } from '../../../stores/session';
import { activeAdminSection, adminLandingPath, visibleAdminMenu, type AdminMenuView } from '../admin-sections';
import { AdminMenu } from '../components/admin-menu/AdminMenu';
import { AdminRail, type AdminRailHandle } from '../components/admin-menu/AdminRail';
import { useAdminMenuCounts } from '../use-admin-menu-counts';
import styles from './AdminLayout.module.css';

/**
 * The Admin frame (Admin redesign 2026-11-02, Option A of `1-shell/admin-frame.html`): a grouped menu on the left
 * and the open page filling all the width beside it.
 *
 * - 1440 and wider, down to the laptop breakpoint: the 248px menu, flush to the left edge.
 * - Laptop (1024): a 72px icon rail; a group's sections open beside its icon.
 * - Tablet and phone (768, 375): an "Admin · People" button that opens the menu as a sheet.
 *
 * Only one of the three is drawn, so a screen reader meets one Admin menu, never three.
 */

/** The breakpoints of semantic.css (`--nr-bp-laptop`, `--nr-bp-tablet`). */
const RAIL_MEDIA = '(max-width: 1180px)';
const SHEET_MEDIA = '(max-width: 900px)';

function useAdminMenu(): AdminMenuView {
  return visibleAdminMenu(useCurrentSession(), useModules());
}

export function AdminLayout(): ReactNode {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const menu = useAdminMenu();
  const counts = useAdminMenuCounts();
  const isRail = useMediaQuery(RAIL_MEDIA);
  const isSheet = useMediaQuery(SHEET_MEDIA);
  const findRef = useRef<HTMLInputElement | null>(null);
  const railRef = useRef<AdminRailHandle | null>(null);
  /** The phone's sheet, and whether it opened from Ctrl+/ and so starts in "Find a setting". */
  const [sheet, setSheet] = useState<{ readonly focusFind: boolean } | null>(null);
  // Someone with no admin rights sees only the refusal, not a menu of doors they cannot open.
  const hasMenu = menu.overview !== null || menu.groups.length > 0;

  // Ctrl+/ finds a setting (FRAME-SPEC §2): plain "/" already focuses a list's own search (06-qol §2). Like
  // Ctrl+K it works from inside a field, because it is how you leave one.
  useEffect(() => {
    if (!hasMenu) return undefined;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (!(event.ctrlKey || event.metaKey) || event.key !== '/') return;
      event.preventDefault();
      if (isSheet) setSheet({ focusFind: true });
      else if (isRail) railRef.current?.openFind();
      else findRef.current?.focus();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [hasMenu, isRail, isSheet]);

  const active = activeAdminSection(pathname, menu);

  return (
    <div className={styles.area}>
      {hasMenu && !isRail && <AdminMenu menu={menu} counts={counts} pathname={pathname} variant="panel" findRef={findRef} />}
      {hasMenu && isRail && !isSheet && <AdminRail ref={railRef} menu={menu} counts={counts} pathname={pathname} />}
      {hasMenu && isSheet && (
        <button
          type="button"
          className={styles.where}
          aria-haspopup="dialog"
          aria-expanded={sheet !== null}
          onClick={() => setSheet({ focusFind: false })}
        >
          <span className={styles.whereText}>Admin{active ? ` · ${active.section.label}` : ''}</span>
          <ChevronDown size={18} aria-hidden />
        </button>
      )}

      <div className={styles.content}>
        <Outlet />
      </div>

      {hasMenu && isSheet && (
        <Drawer isOpen={sheet !== null} onClose={() => setSheet(null)} title="Admin">
          <AdminMenu
            menu={menu}
            counts={counts}
            pathname={pathname}
            variant="sheet"
            autoFocusFind={sheet?.focusFind === true}
            onNavigate={() => setSheet(null)}
          />
        </Drawer>
      )}
    </div>
  );
}

/**
 * `/admin`: Admin lands on Overview; anyone else on the first page their menu holds (ADR 0073), as before.
 * An old bookmark of `/admin`, People's address until 2026-11-02, therefore still opens Admin; People itself is
 * now `/admin/users`, one click away.
 */
export function AdminLanding(): ReactNode {
  const landing = adminLandingPath(useAdminMenu());
  if (landing === null) return <ForbiddenState resource="the admin area" contact="Your admin" />;
  return <Navigate to={landing} replace />;
}
